import { apiClient } from 'api/client'

/**
 * Phase 3: マスターデータ取得 API ラッパー。
 *
 * daseruno-api の `/v1/master/*` エンドポイントを呼ぶ薄いラッパー。
 * featureFlags.isRestMasterEnabled() が true のときのみ使われる。
 * エラーは呼び出し元 (slice thunk) で catch する。
 */

export interface ApiIngredientCategory {
  id: number
  firestore_document_id: string | null
  label_name: string
  sort_order: number
  is_disabled: boolean
}

export interface ApiIngredientSubCategory {
  id: number
  firestore_document_id: string | null
  label_name: string
  ingredient_category_id: number
  ingredient_category_firestore_document_id: string | null
  sort_order: number
  is_disabled: boolean
}

export interface ApiAdditiveUse {
  id: number
  firestore_document_id: string | null
  label_name: string
  sort_order: number
  is_disabled: boolean
  is_not_displayed_to_bread_label: boolean
}

export const fetchIngredientCategories = async (): Promise<
  ApiIngredientCategory[]
> => {
  const res = await apiClient.get<{ categories: ApiIngredientCategory[] }>(
    '/v1/master/ingredient-categories',
  )
  return res.data.categories
}

export const fetchIngredientSubCategories = async (): Promise<
  ApiIngredientSubCategory[]
> => {
  const res = await apiClient.get<{
    sub_categories: ApiIngredientSubCategory[]
  }>('/v1/master/ingredient-sub-categories')
  return res.data.sub_categories
}

export const fetchAdditiveUses = async (): Promise<ApiAdditiveUse[]> => {
  const res = await apiClient.get<{ additive_uses: ApiAdditiveUse[] }>(
    '/v1/master/additive-uses',
  )
  return res.data.additive_uses
}

// ========================================
// Wholesalers
// ========================================

export interface ApiWholesaler {
  id: number
  /** Firestore wholesalers doc ID。Web スライスの `id` (string) フィールドに対応 */
  firestore_document_id: string | null
  name: string
  is_active: boolean
  created_at: string | null
  updated_at: string | null
}

export const fetchWholesalers = async (
  includeInactive = false,
): Promise<ApiWholesaler[]> => {
  const res = await apiClient.get<{ wholesalers: ApiWholesaler[] }>(
    '/v1/wholesalers',
    { params: includeInactive ? { include_inactive: 'true' } : {} },
  )
  return res.data.wholesalers
}

export const fetchWholesalerById = async (
  firestoreId: string,
): Promise<ApiWholesaler | null> => {
  try {
    const res = await apiClient.get<ApiWholesaler>(
      `/v1/wholesalers/${firestoreId}`,
    )
    return res.data
  } catch {
    return null
  }
}

export const createWholesalerApi = async (
  name: string,
): Promise<ApiWholesaler | null> => {
  const res = await apiClient.post<ApiWholesaler>('/v1/wholesalers', { name })
  return res.data
}

export const updateWholesalerApi = async (
  firestoreId: string,
  data: { name?: string; is_active?: boolean },
): Promise<ApiWholesaler | null> => {
  const res = await apiClient.patch<ApiWholesaler>(
    `/v1/wholesalers/${firestoreId}`,
    data,
  )
  return res.data
}
